import { MealSwapFilterOption, MealTime } from "@/clients";
import { PlansService } from "@/clients/services/PlansService";
import { QueryKeys } from "@/constants/query-keys";
import { QueryStaleTime } from "@/constants/query-stale-time";
import { guardGetUserFromLocalStorage } from "@/utils/request";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

export type MealTypeCategory = "recipes" | "branded" | "outAndAbout";

export const useGetAlternativeFilterOptions = (
  mealTime?: MealTime,
  format: "v2" | undefined = "v2",
) => {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: [QueryKeys.PLAN_ALTERNATIVE_FILTER_OPTIONS, mealTime ?? "", format ?? ""],
    staleTime: QueryStaleTime.VERY_LONG,
    queryFn: async () => {
      const user = guardGetUserFromLocalStorage();
      const { data } =
        await PlansService.getMealAlternativesFilterOptionsApiV1PlansAlternativesFiltersGet(
          user.id,
          mealTime,
          format,
        );
      return data;
    },
  });
  const optionMapping = useMemo(() => {
    const map: Record<string, MealSwapFilterOption> = {};
    data?.forEach((option) => {
      map[option.value] = option;
    });
    return map;
  }, [data]);
  const optionLabelMapping = useMemo(() => {
    const object: Record<string, string> = {};
    data?.forEach((option) => {
      object[option.value] = option.name;
    });
    return object;
  }, [data]);
  const allSubOptionMapping = useMemo(() => {
    const map: Record<string, MealSwapFilterOption> = {};
    data?.forEach((option) => {
      option.subFilters?.forEach((subOption) => {
        map[subOption.value] = subOption;
      });
    });
    return map;
  }, [data]);

  return {
    options: data ?? [],
    optionMapping,
    optionLabelMapping,
    allSubOptionMapping,
    isLoading,
    isError,
    error,
    refetch,
  };
};
